import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../services/api';
import { UNIT_OPTIONS } from '../../utils/unitInference';
import { decodeUnicodeEscapes } from '../../utils/decodeUnicode';

interface FieldDefault {
  name: string;
  display_name: string;
  data_type: string;
  category: string | null;
  unit: string | null;
  default_unit: string | null;
  default_unit_type: string | null;
}

const FieldDefaultsPage: React.FC = () => {
  const [fields, setFields] = useState<FieldDefault[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState<Record<string, { default_unit: string | null; default_unit_type: string | null }>>({});
  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const fetchFields = useCallback(async () => {
    try {
      const resp = await apiClient.get('/v1/admin/field-defaults');
      setFields(resp.data);
    } catch (err) {
      console.error('Failed to fetch field defaults:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFields(); }, [fetchFields]);

  const handleUnitChange = (fieldName: string, unitValue: string) => {
    if (unitValue === '__none__') {
      setChanges(prev => ({ ...prev, [fieldName]: { default_unit: null, default_unit_type: null } }));
    } else if (unitValue === '__dimensionless__') {
      setChanges(prev => ({ ...prev, [fieldName]: { default_unit: null, default_unit_type: 'dimensionless' } }));
    } else {
      const unitType = UNIT_OPTIONS.find(g => g.units.some(u => u.value === unitValue))?.unitType || null;
      setChanges(prev => ({ ...prev, [fieldName]: { default_unit: unitValue, default_unit_type: unitType } }));
    }
  };

  const handleSave = async () => {
    const updates = Object.entries(changes).map(([name, vals]) => ({
      name,
      default_unit: vals.default_unit,
      default_unit_type: vals.default_unit_type,
    }));
    if (updates.length === 0) return;
    setSaving(true);
    try {
      await apiClient.put('/v1/admin/field-defaults', { updates });
      setChanges({});
      await fetchFields();
    } catch (err) {
      console.error('Failed to save field defaults:', err);
    } finally {
      setSaving(false);
    }
  };

  const categories = Array.from(new Set(fields.map(f => f.category || 'uncategorized'))).sort();

  const filtered = fields.filter(f => {
    if (filter && !f.display_name.toLowerCase().includes(filter.toLowerCase()) && !f.name.toLowerCase().includes(filter.toLowerCase())) return false;
    if (categoryFilter !== 'all' && (f.category || 'uncategorized') !== categoryFilter) return false;
    return true;
  });

  const getEffective = (f: FieldDefault) => {
    if (changes[f.name]) return changes[f.name];
    return { default_unit: f.default_unit, default_unit_type: f.default_unit_type };
  };

  const getSelectValue = (f: FieldDefault) => {
    const eff = getEffective(f);
    if (eff.default_unit) return eff.default_unit;
    if (eff.default_unit_type === 'dimensionless') return '__dimensionless__';
    return '__none__';
  };

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading field defaults...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>
            Canonical Field Defaults
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Set the default unit for each field. These are auto-applied when columns are mapped during import.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={Object.keys(changes).length === 0 || saving}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: Object.keys(changes).length > 0 ? 'none' : '1px solid var(--card-border)',
            background: Object.keys(changes).length > 0 ? '#3b82f6' : 'var(--hover-bg)',
            color: Object.keys(changes).length > 0 ? '#fff' : 'var(--text-secondary)',
            fontWeight: 500,
            fontSize: 13,
            cursor: Object.keys(changes).length > 0 ? 'pointer' : 'default',
          }}
        >
          {saving ? 'Saving...' : `Save Changes${Object.keys(changes).length > 0 ? ` (${Object.keys(changes).length})` : ''}`}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Filter fields..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid var(--card-border)',
            background: 'var(--content-bg)',
            color: 'var(--text-primary)',
            fontSize: 13,
            outline: 'none',
          }}
        />
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid var(--card-border)',
            background: 'var(--content-bg)',
            color: 'var(--text-primary)',
            fontSize: 13,
            outline: 'none',
          }}
        >
          <option value="all">All Categories</option>
          {categories.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div style={{ border: '1px solid var(--card-border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--hover-bg)' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 500, borderBottom: '1px solid var(--card-border)' }}>Field</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 500, borderBottom: '1px solid var(--card-border)' }}>Category</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 500, borderBottom: '1px solid var(--card-border)' }}>Default Unit</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 500, borderBottom: '1px solid var(--card-border)' }}>Unit Type</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(f => {
              const eff = getEffective(f);
              const isChanged = !!changes[f.name];
              return (
                <tr key={f.name} style={{ background: isChanged ? 'var(--hover-bg)' : 'transparent', borderBottom: '1px solid var(--card-border)' }}>
                  <td style={{ padding: '8px 16px', color: 'var(--text-primary)' }}>
                    <div style={{ fontWeight: 500 }}>{decodeUnicodeEscapes(f.display_name)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{f.name}</div>
                  </td>
                  <td style={{ padding: '8px 16px', color: 'var(--text-muted)' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: 'var(--hover-bg)',
                      fontSize: 11,
                    }}>
                      {f.category || 'uncategorized'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <select
                      value={getSelectValue(f)}
                      onChange={e => handleUnitChange(f.name, e.target.value)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 5,
                        border: isChanged ? '1px solid #3b82f6' : '1px solid var(--card-border)',
                        background: 'var(--content-bg)',
                        color: 'var(--text-primary)',
                        fontSize: 13,
                        outline: 'none',
                        minWidth: 120,
                      }}
                    >
                      <option value="__none__">Not specified</option>
                      <option value="__dimensionless__">Dimensionless</option>
                      {UNIT_OPTIONS.map(group => (
                        <optgroup key={group.group} label={group.group}>
                          {group.units.map(u => (
                            <option key={u.value} value={u.value!}>{u.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '8px 16px', color: 'var(--text-muted)', fontSize: 12 }}>
                    {eff.default_unit_type || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>No fields match your filter.</div>
      )}
    </div>
  );
};

export default FieldDefaultsPage;
